// Grants tab component for private profile view
import { useState, useEffect } from 'react';
import { useGrants } from '@/hooks/useGrants';
import { Profile } from '@/hooks/useProfile';
import { GrantCard } from './grants/GrantCard';
import { LoadingScreen } from './LoadingScreen';
import { MapPin, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';

interface GrantsTabProps {
  profile: Profile;
}

export const GrantsTab = ({ profile }: GrantsTabProps) => {
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [locationRequested, setLocationRequested] = useState(false);
  
  const { grants, loading, error, getNearbyGrants } = useGrants({ limit: 10 });

  // Debug logging
  console.log('🎯 GrantsTab rendering:', { 
    profile: !!profile, 
    grantsCount: grants.length, 
    loading, 
    error, 
    userLocation: !!userLocation,
    hasGetNearbyGrants: !!getNearbyGrants
  });

  // Function to request geolocation
  const requestLocation = () => {
    setLocationRequested(true);
    setLocationError(null);

    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by this browser');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setUserLocation({ lat: latitude, lng: longitude });
        console.log('📍 User location acquired for grants:', { latitude, longitude });
      },
      (error) => {
        console.error('Location access denied:', error);
        setLocationError('Location access denied. Please enable location services to see relevant grants.');
      }
    );
  };

  // Get location-based grants when location is available
  useEffect(() => {
    if (userLocation) {
      console.log('🌍 Getting nearby grants for location:', userLocation);
      getNearbyGrants(userLocation.lat, userLocation.lng);
    }
  }, [userLocation, getNearbyGrants]);

  // Auto-request location on first load
  useEffect(() => {
    if (!locationRequested) {
      requestLocation();
    }
  }, [locationRequested]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingScreen />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Error Loading Grants</h3>
          <p className="text-muted-foreground">{error}</p>
        </CardContent>
      </Card>
    );
  }

  if (locationError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Location Required for Grants
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-muted-foreground">
            To show you relevant grants in your area, we need access to your location.
          </p>
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <p className="text-sm text-orange-800">{locationError}</p>
          </div>
          <Button onClick={requestLocation} className="w-full">
            <MapPin className="h-4 w-4 mr-2" />
            Enable Location Access
          </Button>
          <p className="text-xs text-muted-foreground text-center">
            We use your location only to match you with relevant grants in your geographic area.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!grants || grants.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <div className="space-y-4">
            <div className="mx-auto h-12 w-12 bg-primary/10 rounded-full flex items-center justify-center">
              <MapPin className="h-6 w-6 text-primary" />
            </div>
            <h3 className="text-lg font-semibold">No Grants Found</h3>
            <p className="text-muted-foreground max-w-md mx-auto">
              {userLocation 
                ? "We couldn't find any active grants in your area at the moment. Check back later for new opportunities."
                : "Enable location access to see grants relevant to your area."
              }
            </p>
            {!userLocation && (
              <Button onClick={requestLocation}>
                <MapPin className="h-4 w-4 mr-2" />
                Enable Location Access
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Available Grants</h3>
          <p className="text-sm text-muted-foreground">
            {userLocation 
              ? `Found ${grants.length} grant${grants.length !== 1 ? 's' : ''} in your area`
              : `Showing ${grants.length} grant${grants.length !== 1 ? 's' : ''}`
            }
          </p>
        </div>
        {userLocation && (
          <div className="flex items-center text-sm text-green-600">
            <MapPin className="h-4 w-4 mr-1" />
            Location-based
          </div>
        )}
      </div>

      <div className="grid gap-4">
        {grants.map((grant) => (
          <GrantCard key={grant.id} grant={grant} />
        ))}
      </div>

      <div className="text-center">
        <p className="text-xs text-muted-foreground">
          Grant information is updated regularly. Always verify eligibility and requirements 
          directly with the grant provider.
        </p>
      </div>
    </div>
  );
};